import LearningResult from "../model/LearningResult";
import styles from "../css/LearningResultScreen.module.css";
import React, { useEffect, useState } from "react";
import Term from "../../term/Term";
import { useTranslation } from "react-i18next";
import BigButton from "../../common/BigButton";
import { useAppDispatch } from "../../../hook";
import { updatePartialTerms } from "../../term/termSlice";
import BasicMenu, { BasicMenuButton } from "../../common/BasicMenu";
import LoadingScreen from "../../common/LoadingScreen";
import { LearningMode } from "../model/LearningMode";
import LearningCorrectIncorrectCard from "../../common/view/LearningCorrectIncorrectCard";
import LearningDailyCard from "./LearningDailyCard";
import LearningHistoryTermsResult from "../model/LearningHistoryTerms";
import { fetchLearningHistoryTerms } from "../util/learningAPI";
import { LearningHistoryRange } from "../model/learningHistoryRange";
import SpacingView from "../../common/view/SpaingView";
import LearningResultTermListScreen from "./LearningResultTermListScreen";
import { LearningAnswerType } from "../model/LearningAnswerType";
import CountPicker from "../../common/CountPicker";
import { CountPickerStyle } from "../../common/CountPickerStyle";
import { updateLearningDailyTargetCount as updateLearningDailyTargetCountToServer } from "../util/learningAPI";

interface LearningResultScreenProps {
  open: boolean;
  result?: LearningResult;
  onClickContinueButton: () => void;
  onClickReviewCurrentSectionButton: () => void;
  onClickReviewCurrentSectionOnlyIncorrectButton: (terms: Term[]) => void;
  onClickRestartButton: () => void;
  onClickRestartOnlyIncorrectButton: (terms: Term[]) => void;
  onTermDeleted: (deletedTermIds: string[]) => void;
}

export default function LearningResultScreen(props: LearningResultScreenProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const termsDispatch = useAppDispatch();

  const [learningHistoryTermsResult, setLearningHistoryTermsResult] =
    useState<LearningHistoryTermsResult>();

  const [correctTerms, setCorrectTerms] = useState<Term[]>([]);
  const [incorrectTerms, setIncorrectTerms] = useState<Term[]>([]);
  const [terms, setTerms] = useState<Term[]>([]);

  const [selectedAnswerType, setSelectedAnswerType] =
    useState<LearningAnswerType>(LearningAnswerType.correct);

  const [isLoading] = useState(false);

  const [isOpenRestartModeSelectMenu, setIsOpenRestartModeSelectMenu] =
    useState<Term[] | null>(null);
  const [
    isOpenReviewCurrentSectionModeSelectMenu,
    setIsOpenReviewCurrentSectionModeSelectMenu,
  ] = useState<Term[] | null>(null);
  const [
    openReviewSelectionModeSelectMenu,
    setOpenReviewSelectionModeSelectMenu,
  ] = useState<Term[] | null>(null);

  const [showingTermList, setShowingTermList] = useState<boolean>(false);

  const [showingContinueButton, setShowingContinueButton] = useState(true);
  const [showingReviewButton, setShowingReviewButton] = useState(true);
  const [
    showingReviewCurrentSectionButton,
  ] = useState(true);
  const [showingRestartButton, setShowingRestartButton] = useState(true);
  const [showingLearningDailyTargetMenu, setShowingLearningDailyTargetMenu] =
    useState<boolean>(false);
  const [
    showingLearningDailyTargetCountUpdate,
    setShowingLearningDailyTargetCountUpdate,
  ] = useState<boolean>(false);

  const [learningDailyTargetMenuButtons] =
    useState<BasicMenuButton[]>([
      {
        title: t("LearningTodayGoalSettingDialogTitle"),
        handleOnClose: () => setShowingLearningDailyTargetCountUpdate(true),
      },
    ]);

  const reviewCurrentSectionMenuItems = (): BasicMenuButton[] => [
    {
      title: t("LearningReveiwThisSectionAllTermsButton"),
      handleOnClose: () => {
        setIsOpenReviewCurrentSectionModeSelectMenu(null);
        props.onClickReviewCurrentSectionButton();
      },
    },
    {
      title: t("LearningReveiwThisSectionWrongTermsOnlyButton"),
      handleOnClose: () => {
        if (incorrectTerms !== null) {
          props.onClickReviewCurrentSectionOnlyIncorrectButton(incorrectTerms);
        }
        setIsOpenReviewCurrentSectionModeSelectMenu(null);
      },
    },
  ];

  const restartMenuItems = (): BasicMenuButton[] => [
    {
      title: t("LearningResultRestartAllTermsButton"),
      handleOnClose: () => {
        setIsOpenRestartModeSelectMenu(null);
        props.onClickRestartButton();
      },
    },
    {
      title: t("LearningResultRestartOnlyIncorrectTermsButton"),
      handleOnClose: () => {
        const incorrectTerms = isOpenRestartModeSelectMenu;
        if (incorrectTerms !== null) {
          props.onClickRestartOnlyIncorrectButton(incorrectTerms);
        }
        setIsOpenRestartModeSelectMenu(null);
      },
    },
  ];

  const reviewMenuItems = (): BasicMenuButton[] => [
    {
      title: t("LearningResultRestartAllTermsButton"),
      handleOnClose: () => {
        setOpenReviewSelectionModeSelectMenu(null);
        const retryTerms = props.result?.terms;
        if (retryTerms === undefined) {
          return;
        }
        props.onClickRestartOnlyIncorrectButton(retryTerms);
      },
    },
    {
      title: t("LearningResultRestartOnlyIncorrectTermsButton"),
      handleOnClose: () => {
        const incorrectTerms = openReviewSelectionModeSelectMenu;
        if (incorrectTerms !== null) {
          props.onClickRestartOnlyIncorrectButton(incorrectTerms);
        }
        setOpenReviewSelectionModeSelectMenu(null);
      },
    },
  ];

  useEffect(() => {
    if (props.result !== undefined) {
      const newCorrectTerms = props.result.terms.filter(
        (term) => term.isCorrectAnswer
      );
      const newIncorrectTerms = props.result.terms.filter(
        (term) => !term.isCorrectAnswer
      );

      setCorrectTerms(newCorrectTerms);
      setIncorrectTerms(newIncorrectTerms);
      updateShowingButtons();
      fetchLearningHistoryFromServer();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.result]);

  useEffect(() => {
    const newTerms = [...correctTerms, ...incorrectTerms];
    const serializedTerms = newTerms.map((term) => Term.toJson(term));
    setTerms(newTerms);
    termsDispatch(updatePartialTerms(serializedTerms));
  }, [correctTerms, incorrectTerms, termsDispatch]);

  function showDailyGoalMenu(event: React.MouseEvent<HTMLDivElement>) {
    setAnchorEl(event.currentTarget);
    setShowingLearningDailyTargetMenu(true);
  }

  function updateShowingButtons() {
    switch (props.result?.learningMode) {
      case LearningMode.normal:
      case LearningMode.restartOnlyIncorrectTerms:
      case LearningMode.restartFromLearningHistory:
        setShowingReviewButton(false);
        props.result.isLastCards
          ? setShowingContinueButton(false)
          : setShowingRestartButton(false);
        break;
    }
  }

  function handleOnClickRestartButton(
    event?: React.MouseEvent<HTMLDivElement>
  ) {
    const incorrectTerms = props.result?.learnedTerms.filter(
      (term) => !term.isCorrectAnswer
    );
    if (incorrectTerms === undefined) {
      return;
    }

    if (incorrectTerms.length === 0) {
      props.onClickRestartButton();
    } else {
      if (event !== undefined) {
        setAnchorEl(event.currentTarget);
        setIsOpenRestartModeSelectMenu(incorrectTerms);
      }
    }
  }

  function handleOnClickContinueButton() {
    props.onClickContinueButton();
  }

  function handleOnClickReviewButton(event?: React.MouseEvent<HTMLDivElement>) {
    const allTerms = props.result?.terms;
    if (allTerms === undefined) {
      return;
    }

    const incorrectTerms = allTerms.filter((term) => !term.isCorrectAnswer);

    if (incorrectTerms.length === 0) {
      props.onClickRestartOnlyIncorrectButton(allTerms);
    } else {
      if (event !== undefined) {
        setAnchorEl(event.currentTarget);
        setOpenReviewSelectionModeSelectMenu(incorrectTerms);
      }
    }
  }

  function handleOnClickReviewCurrentSectionButton(
    event?: React.MouseEvent<HTMLDivElement>
  ) {
    if (incorrectTerms.length === 0) {
      props.onClickReviewCurrentSectionButton();
    } else {
      if (event !== undefined) {
        setAnchorEl(event.currentTarget);
        setIsOpenReviewCurrentSectionModeSelectMenu(incorrectTerms);
      }
    }
  }

  async function fetchLearningHistoryFromServer() {
    const learningHistoryTerms = await fetchLearningHistoryTerms(
      LearningHistoryRange.day
    );
    setLearningHistoryTermsResult(learningHistoryTerms);
  }

  function deleteTermList(ids: string[], type: LearningAnswerType) {
    const targetList =
      type === LearningAnswerType.correct ? correctTerms : incorrectTerms;
    const setTargetList =
      type === LearningAnswerType.correct ? setCorrectTerms : setIncorrectTerms;

    const deleteIds = new Set(ids);
    const newTerms = targetList.filter((t) => !deleteIds.has(t.id));

    setTargetList(newTerms);
    props.onTermDeleted(ids);
  }

  function updateTermList(termList: Term[], type: LearningAnswerType) {
    const targetList =
      type === LearningAnswerType.correct ? correctTerms : incorrectTerms;
    const setTargetList =
      type === LearningAnswerType.correct ? setCorrectTerms : setIncorrectTerms;

    const updatedIds = new Set(termList.map((t) => t.id));
    const newTerms = targetList.map((t) =>
      updatedIds.has(t.id)
        ? termList.find((updated) => updated.id === t.id)!
        : t
    );

    setTargetList(newTerms);
  }

  function retryCurrentTerms(terms: Term[], type: LearningAnswerType) {
    props.onClickReviewCurrentSectionOnlyIncorrectButton(terms);
  }

  async function updateLearningDailyTargetCount(count: number) {
    await updateLearningDailyTargetCountToServer(count);
    fetchLearningHistoryFromServer();
  }

  if (props.open === false || props.result === undefined) return null;

  return (
    <>
      <div className={styles.stack}>
        <div className={styles.container}>
          <div className={styles.contentContainer}>
            <span className={styles.overViewText}>Overview</span>

            <SpacingView height={16} />

            <div className={styles.correctOrIncorrectContainer}>
              <LearningCorrectIncorrectCard
                showing={true}
                type="correcet"
                termCount={correctTerms.length}
                onClick={() => {
                  setSelectedAnswerType(LearningAnswerType.correct);
                  setShowingTermList(true);
                }}
              />
              <LearningCorrectIncorrectCard
                showing={true}
                type="incorrect"
                termCount={incorrectTerms.length}
                onClick={() => {
                  setSelectedAnswerType(LearningAnswerType.incorrect);
                  setShowingTermList(true);
                }}
              />
            </div>

            <SpacingView height={16} />

            <LearningDailyCard
              showing={learningHistoryTermsResult === undefined}
              value={learningHistoryTermsResult}
              onClick={showDailyGoalMenu}
            />

            <SpacingView height={40} />

            <div className={styles.buttonsContainer}>
              {showingRestartButton === true && (
                <BigButton
                  title={t("LearningResultRestartButton")}
                  variant="normal"
                  handleClick={handleOnClickRestartButton}
                />
              )}
              {showingContinueButton === true && (
                <BigButton
                  title={t("LearningFinishContinueButton")}
                  variant="normal"
                  handleClick={() => handleOnClickContinueButton()}
                />
              )}
              {showingReviewCurrentSectionButton === true && (
                <BigButton
                  title={t("LearningFinishRepeatButton")}
                  variant="clear"
                  handleClick={handleOnClickReviewCurrentSectionButton}
                />
              )}
              {showingReviewButton === true && (
                <BigButton
                  title={t("LearningReviewButton")}
                  variant="clear"
                  handleClick={handleOnClickReviewButton}
                />
              )}
            </div>
          </div>
        </div>

        <BasicMenu
          isOpen={isOpenRestartModeSelectMenu !== null}
          anchorEl={anchorEl}
          buttons={restartMenuItems()}
          handleOnClose={() => {
            setIsOpenRestartModeSelectMenu(null);
          }}
        />

        <BasicMenu
          isOpen={isOpenReviewCurrentSectionModeSelectMenu !== null}
          anchorEl={anchorEl}
          buttons={reviewCurrentSectionMenuItems()}
          handleOnClose={() => {
            setIsOpenReviewCurrentSectionModeSelectMenu(null);
          }}
        />

        <BasicMenu
          isOpen={openReviewSelectionModeSelectMenu !== null}
          anchorEl={anchorEl}
          buttons={reviewMenuItems()}
          handleOnClose={() => {
            setOpenReviewSelectionModeSelectMenu(null);
          }}
        />

        <BasicMenu
          isOpen={showingLearningDailyTargetMenu}
          anchorEl={anchorEl}
          buttons={learningDailyTargetMenuButtons}
          handleOnClose={() => {
            setShowingLearningDailyTargetMenu(false);
          }}
        />

        <CountPicker
          open={showingLearningDailyTargetCountUpdate}
          defaultValue={learningHistoryTermsResult?.goalCount ?? 30}
          style={CountPickerStyle.learningGoalCount}
          onComplete={(count: number) => updateLearningDailyTargetCount(count)}
          onClose={() => {
            setShowingLearningDailyTargetCountUpdate(false);
          }}
        />

        <LearningResultTermListScreen
          showing={showingTermList}
          termList={terms}
          type={selectedAnswerType}
          onUpdateTermList={updateTermList}
          onDeleteTermList={deleteTermList}
          onTapRetryButton={retryCurrentTerms}
          onClose={() => {
            setShowingTermList(false);
          }}
        />

        {isLoading === true && <LoadingScreen />}
      </div>
    </>
  );
}
